import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  ArrowUpTrayIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { quotationService } from '../../services/quotationService';
import { customerService } from '../../services/customerService';
import { productService } from '../../services/productService';
import { settingService } from '../../services/settingService';
import { getMediaUrl } from '../../utils/media';
import toast from 'react-hot-toast';

interface QuotationItemImage {
  image_url: string;
  caption?: string;
}

interface QuotationItemSpec {
  spec_name: string;
  spec_value: string;
}

interface QuotationItem {
  id: string;
  product_id?: number;
  description: string;
  quantity: number;
  unit_price: number;
  discount_amount: number;
  images: QuotationItemImage[];
  specifications: QuotationItemSpec[];
}

function calcVAT(net: number, vatType: string, taxPercent: number) {
  if (vatType === 'vat_exclusive') return { tax: net * taxPercent / 100, total: net * (1 + taxPercent / 100) };
  if (vatType === 'vat_inclusive') return { tax: net - net / (1 + taxPercent / 100), total: net };
  return { tax: 0, total: net };
}

export default function CreateQuotationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState('');
  const [items, setItems] = useState<QuotationItem[]>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('');
  const [termsLoaded, setTermsLoaded] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [vatType, setVatType] = useState<'zero_vat' | 'vat_exclusive' | 'vat_inclusive'>('zero_vat');
  const [taxPercent, setTaxPercent] = useState(16);
  
  // Product search for adding items
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showProductSearch, setShowProductSearch] = useState(false);

  // Add customer dialog
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const defaultNewCustomer = { customer_type: 'individual', first_name: '', last_name: '', company_name: '', email: '', phone: '', group_id: '' };
  const [newCustomer, setNewCustomer] = useState(defaultNewCustomer);

  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll({ limit: 100 }),
  });

  const { data: settingsData, isFetching: isSettingsFetching } = useQuery({
    queryKey: ['settings-documents'],
    queryFn: () => settingService.getAllSettings(),
    staleTime: 0,
  });

  useEffect(() => {
    if (termsLoaded || isSettingsFetching || !settingsData) return;
    const rows: any[] = settingsData?.data?.data || [];
    const def = rows.find((r: any) => r.setting_key === 'default_quotation_terms')?.setting_value || '';
    setTerms(def);
    setDefaultTerms(def);
    setTermsLoaded(true);
  }, [settingsData, isSettingsFetching]);

  const { data: groupsData } = useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => customerService.getGroups(),
    enabled: showAddCustomer,
  });
  const groups = groupsData?.data?.data || [];

  const addCustomerMutation = useMutation({
    mutationFn: (data: any) => customerService.create(data),
    onSuccess: (res: any) => {
      const created = res?.data?.data;
      if (created) {
        setSelectedCustomer(created);
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      }
      toast.success('Customer added');
      setShowAddCustomer(false);
      setNewCustomer(defaultNewCustomer);
      setCustomerSearch('');
      setShowCustomerSearch(false);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to add customer'),
  });

  const handleAddCustomer = () => {
    if (!newCustomer.phone.trim()) { toast.error('Phone is required'); return; }
    if (!newCustomer.group_id) { toast.error('Please select a customer group'); return; }
    if (newCustomer.customer_type === 'individual' && !newCustomer.first_name && !newCustomer.last_name) {
      toast.error('Enter at least a first or last name'); return;
    }
    if (newCustomer.customer_type === 'company' && !newCustomer.company_name) {
      toast.error('Company name is required'); return;
    }
    addCustomerMutation.mutate({
      customer_type: newCustomer.customer_type,
      first_name: newCustomer.first_name || null,
      last_name: newCustomer.last_name || null,
      company_name: newCustomer.company_name || null,
      email: newCustomer.email || null,
      phone: newCustomer.phone.trim(),
      group_id: Number(newCustomer.group_id),
      country: 'Kenya',
    });
  };

  const createMutation = useMutation({
    mutationFn: (data: any) => quotationService.create(data),
    onSuccess: () => {
      toast.success('Quotation created successfully');
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      navigate('/quotations');
    },
    onError: () => {
      toast.error('Failed to create quotation');
    },
  });

  const customers = customersData?.data?.data?.items || [];
  const filteredCustomers = customerSearch
    ? customers.filter((c: any) => {
        const search = customerSearch.toLowerCase();
        const name = c.company_name || `${c.first_name} ${c.last_name}`;
        return name.toLowerCase().includes(search);
      })
    : [];

  const handleSearchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await productService.search(query);
      setSearchResults(response.data?.data || []);
    } catch {
      setSearchResults([]);
    }
  };

  const addItem = (product?: any) => {
    const newItem: QuotationItem = {
      id: `item-${Date.now()}`,
      product_id: product?.product_id,
      description: product?.product_name || 'Custom Item',
      quantity: 1,
      unit_price: product?.price_history?.[0]?.selling_price || 0,
      discount_amount: 0,
      images: [],
      specifications: [],
    };
    setItems([...items, newItem]);
    setProductSearch('');
    setSearchResults([]);
    setShowProductSearch(false);
  };

  const removeItem = (id: string) => {
    setItems(items.filter(i => i.id !== id));
  };

  const updateItem = (id: string, field: keyof QuotationItem, value: any) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingImage, setUploadingImage] = useState<string | null>(null);

  const handleFileSelect = async (itemId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(itemId);
    try {
      const response = await quotationService.uploadImage(file);
      const imageUrl = response.data?.data?.image_url;
      if (imageUrl) {
        // Use functional update to avoid stale closure
        setItems(prevItems => prevItems.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              images: [...item.images, { image_url: imageUrl, caption: file.name }],
            };
          }
          return item;
        }));
      } else {
        toast.error('Upload failed: no URL returned');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  const removeImage = (itemId: string, index: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        return { ...item, images: item.images.filter((_, i) => i !== index) };
      }
      return item;
    }));
  };

  const addSpec = (itemId: string) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          specifications: [...item.specifications, { spec_name: '', spec_value: '' }],
        };
      }
      return item;
    }));
  };

  const updateSpec = (itemId: string, index: number, field: keyof QuotationItemSpec, value: string) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const newSpecs = [...item.specifications];
        newSpecs[index] = { ...newSpecs[index], [field]: value };
        return { ...item, specifications: newSpecs };
      }
      return item;
    }));
  };

  const removeSpec = (itemId: string, index: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        return { ...item, specifications: item.specifications.filter((_, i) => i !== index) };
      }
      return item;
    }));
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const itemDiscounts = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const overallDiscount = discountPercent > 0 ? subtotal * (discountPercent / 100) : 0;
  const totalDiscount = itemDiscounts + overallDiscount;
  const net = subtotal - totalDiscount;
  const { tax: taxAmount, total } = calcVAT(net, vatType, taxPercent);

  const handleSubmit = () => {
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    createMutation.mutate({
      customer_id: selectedCustomer?.customer_id,
      customer_name: selectedCustomer ? undefined : 'Walk-in Customer',
      quotation_date: quotationDate,
      expiry_date: expiryDate || undefined,
      items: items.map(item => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount_amount: item.discount_amount,
        images: item.images.filter(img => img.image_url.trim() !== ''),
        specifications: item.specifications.filter(spec => spec.spec_name.trim() !== ''),
      })),
      discount_percent: discountPercent,
      vat_type: vatType,
      tax_percent: taxPercent,
      tax_amount: taxAmount,
      notes,
      terms_conditions: terms,
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">New Quotation</h1>
        <button
          onClick={() => navigate('/quotations')}
          className="text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Customer Selection */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Customer</label>
              <button
                type="button"
                onClick={() => setShowAddCustomer(true)}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                <UserPlusIcon className="w-3.5 h-3.5" />
                Add Customer
              </button>
            </div>
            {selectedCustomer ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">
                    {selectedCustomer.company_name || 
                      `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}
                  </p>
                  {selectedCustomer.email && (
                    <p className="text-sm text-gray-500">{selectedCustomer.email}</p>
                  )}
                </div>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="text-red-600 hover:text-red-800"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerSearch(true);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                {showCustomerSearch && customerSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {filteredCustomers.map((customer: any) => (
                      <button
                        key={customer.customer_id}
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setCustomerSearch('');
                          setShowCustomerSearch(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50"
                      >
                        <p className="font-medium">
                          {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                        </p>
                        {customer.email && (
                          <p className="text-sm text-gray-500">{customer.email}</p>
                        )}
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <p className="px-4 py-2 text-sm text-gray-500">No customers found</p>
                    )}
                    <button
                      type="button"
                      onClick={() => { setShowAddCustomer(true); setShowCustomerSearch(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-primary-600 hover:bg-primary-50 border-t border-gray-100 font-medium"
                    >
                      <UserPlusIcon className="w-4 h-4" />
                      Add new customer
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quotation Date
              </label>
              <input
                type="date"
                value={quotationDate}
                onChange={(e) => setQuotationDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <label className="block text-sm font-medium text-gray-700 whitespace-nowrap">Items</label>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  handleSearchProducts(e.target.value);
                  setShowProductSearch(true);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
              />
              {showProductSearch && searchResults.length > 0 && (
                <div className="absolute right-0 z-10 w-80 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {searchResults.map((product: any) => (
                    <button
                      key={product.product_id}
                      onClick={() => addItem(product)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50"
                    >
                      <p className="font-medium">{product.product_name}</p>
                      <p className="text-sm text-gray-500">SKU: {product.sku}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => addItem()}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add Custom Item
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {items.length > 0 && (
              <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 rounded-t-lg">
                <div className="col-span-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</div>
                <div className="col-span-1 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Units</div>
                <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Unit Price</div>
                <div className="col-span-1 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Discount</div>
                <div className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Amount</div>
                <div className="col-span-2"></div>
              </div>
            )}
            {items.map((item) => (
              <div key={item.id} className="bg-white">
                <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
                  <div className="col-span-4">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Item description"
                    />
                  </div>
                  <div className="col-span-1">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min="0"
                      value={item.unit_price}
                      onChange={(e) => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                    />
                  </div>
                  <div className="col-span-1">
                    <input
                      type="number"
                      min="0"
                      value={item.discount_amount}
                      onChange={(e) => updateItem(item.id, 'discount_amount', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                    />
                  </div>
                  <div className="col-span-2 text-right text-sm font-medium">
                    KSh {((item.quantity * item.unit_price) - item.discount_amount).toLocaleString()}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-2">
                    <button
                      onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                      className="text-xs text-primary-600 hover:text-primary-800"
                    >
                      {expandedItem === item.id ? 'Less' : 'More'}
                    </button>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {expandedItem === item.id && (
                  <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-6 mt-3">
                      {/* Images */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Photos</label>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={(el) => { fileInputRefs.current[item.id] = el; }}
                            onChange={(e) => handleFileSelect(item.id, e)}
                          />
                          <button
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                            disabled={uploadingImage === item.id}
                            className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50"
                          >
                            {uploadingImage === item.id ? (
                              <>
                                <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <ArrowUpTrayIcon className="w-3 h-3" />
                                Upload Photo
                              </>
                            )}
                          </button>
                        </div>
                        {item.images.length > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            {item.images.map((img, idx) => (
                              <div key={idx} className="relative group">
                                <img
                                  src={getMediaUrl(img.image_url)}
                                  alt={img.caption || 'Quotation image'}
                                  className="h-20 w-20 object-cover rounded border border-gray-200"
                                />
                                <button
                                  onClick={() => removeImage(item.id, idx)}
                                  className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                >
                                  <XMarkIcon className="w-3 h-3" />
                                </button>
                                {img.caption && (
                                  <p className="text-xs text-gray-500 mt-0.5 max-w-[80px] truncate">{img.caption}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {item.images.length === 0 && (
                          <div
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors"
                          >
                            <PhotoIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                            <p className="text-xs text-gray-500">Click to upload images</p>
                          </div>
                        )}
                      </div>
                      {/* Specifications */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Specifications</label>
                          <button
                            onClick={() => addSpec(item.id)}
                            className="text-xs text-primary-600 hover:text-primary-800"
                          >
                            + Add Spec
                          </button>
                        </div>
                        {item.specifications.map((spec, idx) => (
                          <div key={idx} className="flex gap-2 mb-2">
                            <input
                              type="text"
                              placeholder="Name (e.g. Color)"
                              value={spec.spec_name}
                              onChange={(e) => updateSpec(item.id, idx, 'spec_name', e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              type="text"
                              placeholder="Value (e.g. Red)"
                              value={spec.spec_value}
                              onChange={(e) => updateSpec(item.id, idx, 'spec_value', e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <button
                              onClick={() => removeSpec(item.id, idx)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        {item.specifications.length === 0 && (
                          <p className="text-xs text-gray-400">No specifications added</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                No items added. Search for products or add custom items.
              </div>
            )}
          </div>
        </div>

        {/* VAT Type */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">VAT Type</label>
          <div className="flex gap-4">
            {(['zero_vat', 'vat_exclusive', 'vat_inclusive'] as const).map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="vat_type"
                  value={type}
                  checked={vatType === type}
                  onChange={() => setVatType(type)}
                  className="text-blue-600"
                />
                <span className="text-sm">
                  {type === 'zero_vat' ? 'Zero VAT' : type === 'vat_exclusive' ? 'VAT Exclusive' : 'VAT Inclusive'}
                </span>
              </label>
            ))}
          </div>
          {vatType !== 'zero_vat' && (
            <div className="flex items-center gap-2 mt-1">
              <label className="text-sm text-gray-600">Rate:</label>
              <input
                type="number"
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value))}
                className="w-20 border rounded px-2 py-1 text-sm"
                min={0}
                max={100}
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-72 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span>KSh {subtotal.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Discount (%):</span>
              <input
                type="number"
                min="0"
                max="100"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(parseFloat(e.target.value) || 0)}
                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right"
              />
            </div>
            {overallDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount Amount:</span>
                <span className="text-red-600">-KSh {overallDiscount.toLocaleString()}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span>VAT ({taxPercent}%)</span>
                <span>KSh {taxAmount.toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
              <span>Total:</span>
              <span>KSh {total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Notes & Terms */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Additional notes for the customer..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions
            </label>
            <textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Payment terms, delivery conditions, etc..."
            />
            {terms && terms !== defaultTerms && (
              <p className="mt-1 text-xs text-amber-600">
                Differs from default.{' '}
                <button
                  type="button"
                  className="underline font-medium hover:text-amber-800"
                  onClick={() =>
                    settingService.updateSetting('default_quotation_terms', { value: terms })
                      .then(() => { setDefaultTerms(terms); queryClient.invalidateQueries({ queryKey: ['settings-documents'] }); toast.success('Default quotation T&C updated'); })
                      .catch(() => toast.error('Failed to update default'))
                  }
                >
                  Save as new default?
                </button>
              </p>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/quotations')}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending || items.length === 0}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Quotation'}
          </button>
        </div>
      </div>

      {/* Add Customer Dialog */}
      {showAddCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Add New Customer</h2>
              <button onClick={() => setShowAddCustomer(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Type */}
            <div className="flex gap-4">
              {(['individual', 'company'] as const).map((t) => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value={t}
                    checked={newCustomer.customer_type === t}
                    onChange={() => setNewCustomer(p => ({ ...p, customer_type: t }))}
                    className="text-primary-600"
                  />
                  <span className="text-sm capitalize">{t}</span>
                </label>
              ))}
            </div>

            {newCustomer.customer_type === 'company' ? (
              <input
                type="text"
                placeholder="Company name *"
                value={newCustomer.company_name}
                onChange={(e) => setNewCustomer(p => ({ ...p, company_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              />
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First name"
                  value={newCustomer.first_name}
                  onChange={(e) => setNewCustomer(p => ({ ...p, first_name: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={newCustomer.last_name}
                  onChange={(e) => setNewCustomer(p => ({ ...p, last_name: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            <input
              type="tel"
              placeholder="Phone *"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer(p => ({ ...p, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer(p => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={newCustomer.group_id}
              onChange={(e) => setNewCustomer(p => ({ ...p, group_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select customer group *</option>
              {groups.map((g: any) => (
                <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
              ))}
            </select>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowAddCustomer(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                disabled={addCustomerMutation.isPending}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {addCustomerMutation.isPending ? 'Saving...' : 'Save Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
